import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, Clock, Euro, Zap, Bookmark, Send, ShieldCheck, BookmarkX } from "lucide-react";
import { toast } from "sonner";

export default function SavedJobs() {
    const nav = useNavigate();
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        const { data } = await api.get("/matching/saved");
        setJobs(data.data || []);
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const apply = async (id) => {
        try {
            await api.post("/applications", { job_id: id });
            toast.success("Candidatura inviata!");
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const unsave = async (id) => {
        await api.post(`/matching/unsave/${id}`);
        load();
    };

    return (
        <div className="max-w-6xl mx-auto">
            <div className="mb-6">
                <h1 className="font-display text-3xl font-bold tracking-tight">Opportunità salvate</h1>
                <p className="text-sm text-[#5C5C5C] mt-1">{jobs.length} bookmark · torna quando vuoi per candidarti.</p>
            </div>

            {loading && <div className="text-sm text-[#5C5C5C]">Caricamento…</div>}
            {!loading && jobs.length === 0 && (
                <div className="hl-card p-10 text-center">
                    <Bookmark className="w-10 h-10 mx-auto text-[#1C3F39] mb-3" />
                    <div className="font-display text-xl">Nessuna opportunità salvata</div>
                    <div className="text-sm text-[#5C5C5C] mt-2">Salva le opportunità dal feed per ritrovarle qui.</div>
                </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
                {jobs.map((j) => (
                    <div key={j.id} className="hl-card p-6" data-testid={`saved-job-${j.id}`}>
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-3">
                                {j.facility_logo_url && (
                                    <img src={`${process.env.REACT_APP_BACKEND_URL}${j.facility_logo_url}`}
                                        alt="" className="w-12 h-12 rounded-md object-cover border border-[#E5E5E0]" />
                                )}
                                <div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="hl-pill">{j.profession}</span>
                                        {j.urgent && <span className="hl-pill bg-[#FDF4F2] text-[#D96C5B] border-[#D96C5B]/30"><Zap className="w-3 h-3" />Urgente</span>}
                                    </div>
                                    <h3 className="mt-3 font-display text-lg font-bold tracking-tight">{j.title}</h3>
                                    <div className="text-sm text-[#5C5C5C] flex items-center gap-1">
                                        {j.facility_name}
                                        {j.facility_verified && <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />}
                                    </div>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-xs text-[#5C5C5C]">Match</div>
                                <div className="font-display text-2xl font-bold text-[#1C3F39]">{j.match_score}%</div>
                            </div>
                        </div>
                        <div className="mt-4 grid grid-cols-2 gap-3 text-sm text-[#5C5C5C]">
                            <div className="flex items-center gap-2"><MapPin className="w-4 h-4" />{j.city}{j.distance_km != null && ` · ${j.distance_km} km`}</div>
                            <div className="flex items-center gap-2"><Clock className="w-4 h-4" />{j.date} · {j.start_time}–{j.end_time}</div>
                            <div className="flex items-center gap-2 text-[#1A1A1A] font-semibold col-span-2">
                                <Euro className="w-4 h-4" /><span className="font-mono">{j.compensation_gross?.toFixed(2)}€</span> <span className="text-xs font-normal text-[#5C5C5C]">lordo</span>
                            </div>
                        </div>
                        <div className="mt-5 flex items-center gap-2">
                            <Button onClick={() => apply(j.id)} disabled={j.already_applied}
                                className="bg-[#1C3F39] hover:bg-[#2A5C53]" data-testid={`apply-saved-${j.id}`}>
                                <Send className="w-4 h-4 mr-2" />{j.already_applied ? "Candidato" : "Candidati"}
                            </Button>
                            <Button variant="outline" onClick={() => nav(`/app/job/${j.id}`)} data-testid={`view-saved-${j.id}`}>Dettagli</Button>
                            <Button variant="ghost" size="icon" onClick={() => unsave(j.id)} data-testid={`unsave-${j.id}`} title="Rimuovi dai salvati">
                                <BookmarkX className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
